import { useQueries } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { SuggestionsApi } from 'src/services/suggestionsApi'

const fetchKeywordVariations = async (
  keyword: string,
  language: string
): Promise<string[]> => {
  if (!keyword.trim()) return []

  const { data } =
    await SuggestionsApi.v1Private.suggestionsKeywordVariationsList({
      keyword,
      language,
    })

  return data.variations ?? []
}

/**
 * Fetch keyword variations for one or more languages and merge them. Each
 * language is a separate query (and a separate Redis cache key on the backend),
 * so already-generated languages are served from cache and only newly added
 * languages trigger generation.
 */
export const useGetKeywordVariationsQuery = (
  keyword: string,
  languages: string[],
  enabled = true
) => {
  const trimmed = keyword.trim()

  const results = useQueries({
    queries: languages.map((language) => ({
      enabled: enabled && !!trimmed,
      queryFn: () => fetchKeywordVariations(trimmed, language),
      queryKey: [QueryKey.SUGGEST_WORD, trimmed, 'variations', language],
    })),
  })

  const variations = Array.from(
    new Set(results.flatMap((result) => (result.data as string[]) ?? []))
  )

  return {
    isLoading: results.some((result) => result.isLoading),
    variations,
  }
}
